import { useState, useEffect, useRef } from "react";
import {
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaLinkedinIn,
  FaChevronDown,
} from "react-icons/fa";
import { X, User, LayoutDashboard, LogOut } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useNityaSevaMember } from "../context/NityaSevaMemberContext";
import "../styles/demoHeader.css";

// ─── DATA ─────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { label: "Home", href: "/" },
  {
    label: "About",
    href: "#",
    children: [
      { label: "ISKCON Delhi", href: "/about/iskcon-delhi" },
      { label: "What is ISKCON?", href: "/about/what-is-iskcon" },
      { label: "Srila Prabhupada", href: "/about/srila-prabhupada" },
    ],
  },
  {
    label: "Donate",
    href: "#",
    children: [
      { label: "Food for Life", href: "/donate/food-for-life" },
      {
        label: "Nitya Seva",
        href: "/donate/nitya-sevak-registration",
        special: true,
      },
      { label: "Brahmana Bhoj Seva", href: "/donate/brahmana-bhoja-seva" },
      { label: "Sri Vigraha Seva", href: "/donate/sri-vigraha-seva" },
      { label: "Sadhu Seva", href: "/donate/sadhu-seva" },
      // { label: "Janmashtami Seva", href: "/sri-krishna-janmashtami-2026" },
      { label: "Rajbhog Seva", href: "/donate/rajbhoga-seva" },
      { label: "Other Seva", href: "/donate/other-seva" },
      { label: "Life Time Membership", href: "/lifetime-membership" },
    ],
  },
  {
    label: "Booking",
    href: "#",
    children: [
      { label: "Kirtan", href: "/booking/kirtan" },
      { label: "Offer Puja", href: "/booking/offer-puja" },
    ],
  },
  { label: "Blog", href: "/blog" },
  { label: "Gallery", href: "/gallery" },
  { label: "Contact", href: "/contact" },
];

// Top utility bar links (marquee + right side quick links)
const UTILITY_LINKS = [
  {
    label: "Nitya Sewa",
    href: "/donate/nitya-sevak-registration",
    special: true,
  },
  { label: "Offer Puja", href: "/booking/offer-puja" },
  { label: "Kirtan", href: "/booking/kirtan" },
  {
    label: "Live Darshan",
    href: "https://www.youtube.com/watch?v=-TmjOEDbzpA",
    external: true,
  },
  { label: "Blog", href: "/blog" },
  { label: "Gallery", href: "/gallery" },
  { label: "Quick Donate", href: "/quick-donation" },
];

const SOCIAL_LINKS = [
  {
    icon: FaFacebookF,
    href: "https://www.facebook.com/IskconDelhiOfferings/",
    label: "Facebook",
  },
  {
    icon: FaTwitter,
    href: "https://twitter.com/IskconOfferings",
    label: "Twitter",
  },
  {
    icon: FaYoutube,
    href: "https://www.youtube.com/channel/UCLb7-Rq2RA7OuqjNrqwHXjg",
    label: "YouTube",
  },
  {
    icon: FaInstagram,
    href: "https://www.instagram.com/iskcondelhiofferings/",
    label: "Instagram",
  },
];

// ─── HEADER COMPONENT ─────────────────────────────────────────────────────────

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);

  // ── Nitya Sevak account dropdown (profile icon + Dashboard/Logout) ──────
  const { isLoggedIn, profile, logout } = useNityaSevaMember();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const navigate = useNavigate();

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Close account dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Logged-out ho jaye tabhi dropdown band ho jaye (safety)
  useEffect(() => {
    if (!isLoggedIn) setAccountOpen(false);
  }, [isLoggedIn]);

  function toggleAccordion(label) {
    setOpenAccordion(openAccordion === label ? null : label);
  }

  function closeMobileMenu() {
    setMobileMenuOpen(false);
    setOpenAccordion(null);
  }

  async function handleLogout() {
    setAccountOpen(false);
    await logout();
    navigate("/");
  }

  return (
    <>
      {/* ── MAIN HEADER ─────────────────────────────────────────────── */}
      <header className="header">
        {/* ── LEFT LOGO BOX ──────────────────────────────────────────── */}
        <div className="header__logo-box">
          <Link
            to="/"
            className="header__logo-link"
            aria-label="ISKCON Delhi Home"
          >
            <div className="header__logo-ring">
              <img
                loading="lazy"
                src={`${process.env.PUBLIC_URL}/assets/images/iskcon.png`}
                alt="ISKCON Delhi"
                className="header__logo-img"
              />
            </div>
          </Link>
        </div>

        {/* ── RIGHT SECTION (Top bar + Nav bar) ─────────────────────── */}
        <div className="header__right">
          {/* Row 1: Utility links marquee + Social icons */}
          <div className="header__top-bar">
            {/* Maroon diagonal strip containing utility links */}
            <div className="header__utility-strip">
              <div className="header__marquee-wrapper">
                <div className="header__marquee-track">
                  {/* Duplicated for infinite scroll illusion */}
                  {[...UTILITY_LINKS].map((link, index) => (
                    <span className="header__marquee-item" key={index}>
                      <Link
                        to={link.href}
                        target={link.external ? "_blank" : undefined}
                        rel={link.external ? "noreferrer" : undefined}
                        className={
                          link.special
                            ? "header__marquee-link is-special"
                            : "header__marquee-link"
                        }
                      >
                        {link.label}
                      </Link>
                      <span className="header__marquee-dot">●</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Social icons on the right */}
            <div className="header__social-icons">
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  className="header__social-link"
                  aria-label={social.label}
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>

          {/* Row 2: Main nav links + Sri Ram Navami badge + Hamburger */}
          <div className="header__nav-bar">
            {/* Desktop nav list */}
            <ul className="header__nav-list">
              {NAV_LINKS.map((item) => (
                <li key={item.label} className="header__nav-item">
                  {/* Nav item WITH dropdown */}
                  {item.children ? (
                    <>
                      <button className="header__nav-link">
                        {item.label}
                        <FaChevronDown className="header__nav-caret" />
                      </button>

                      {/* Dropdown menu */}
                      <div className="header__dropdown">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className={
                              child.special
                                ? "header__dropdown-link is-special"
                                : "header__dropdown-link"
                            }
                          >
                            <span className="header__dropdown-marker">›</span>
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    /* Nav item — regular link */
                    <Link to={item.href} className="header__nav-link">
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Sri Ram Navami badge */}
            {/* <Link
              to="/sri-krishna-janmashtami-2026"
              className="header__festival-badge"
            >
              <span className="header__festival-dot"></span>
              Sri Krishna Janmashtami
            </Link> */}

            {/* TODO : NEWLY ADDED JANMASHTAMI LINK */}
            {/* <Link
              to="/sri-krishna-janmashtami-2026"
              className="festival-pill d-none d-lg-flex"
            >
              <span className="festival-pill__dot"></span>

              <div className="festival-pill__content">

                <span className="festival-pill__title">
                  Sri Krishna Janmashtami
                </span>
              </div>
            </Link> */}

            {/* Nitya Sevak account — profile icon + dropdown (Dashboard / Logout) */}
            {isLoggedIn && (
              <div className="header__account" ref={accountRef}>
                <button
                  type="button"
                  className="header__account-trigger"
                  onClick={() => setAccountOpen((prev) => !prev)}
                  aria-label="Account menu"
                  aria-expanded={accountOpen}
                >
                  <User size={18} />
                </button>

                <div
                  className={`header__account-dropdown ${accountOpen ? "is-open" : ""}`}
                >
                  <div className="header__account-name">
                    {profile?.firstName
                      ? `${profile.firstName} ${profile.lastName || ""}`.trim()
                      : "Nitya Sevak"}
                  </div>

                  <Link
                    to="/nitya-seva/dashboard"
                    className="header__account-link"
                    onClick={() => setAccountOpen(false)}
                  >
                    <LayoutDashboard size={16} />
                    Dashboard
                  </Link>

                  <button
                    type="button"
                    className="header__account-link header__account-logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              </div>
            )}

            {/* Hamburger button — visible only on mobile */}
            <button
              className={`header__hamburger ${mobileMenuOpen ? "is-open" : ""}`}
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <span className="header__hamburger-bar"></span>
              <span className="header__hamburger-bar"></span>
              <span className="header__hamburger-bar"></span>
            </button>
          </div>
        </div>
      </header>

      {/* ── MOBILE MENU ─────────────────────────────────────────────── */}
      <div
        className={`mobile-menu ${mobileMenuOpen ? "is-open" : ""}`}
        aria-hidden={!mobileMenuOpen}
      >
        {/* Top: Brand + Close button */}
        <div className="mobile-menu__header">
          <span className="mobile-menu__brand">ISKCON DELHI</span>
          <button
            className="mobile-menu__close-btn"
            onClick={closeMobileMenu}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Decorative divider */}
        <div className="mobile-menu__divider">✦ ✦ ✦</div>

        {/* Nav links */}
        <nav className="mobile-menu__nav">
          {NAV_LINKS.map((item) => (
            <div key={item.label} className="mobile-menu__nav-item">
              {/* Item WITH sub-links (accordion) */}
              {item.children ? (
                <>
                  <button
                    className="mobile-menu__nav-link"
                    onClick={() => toggleAccordion(item.label)}
                  >
                    {item.label}
                    <FaChevronDown
                      className={`mobile-menu__nav-caret ${openAccordion === item.label ? "is-open" : ""}`}
                    />
                  </button>

                  {/* Sub-links */}
                  <div
                    className={`mobile-menu__sub-links ${openAccordion === item.label ? "is-open" : ""}`}
                  >
                    {item.children.map((child) => (
                      <Link
                        key={child.label}
                        to={child.href}
                        className="mobile-menu__sub-link"
                        onClick={closeMobileMenu}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                /* Regular link */
                <Link
                  to={item.href}
                  className="mobile-menu__nav-link"
                  onClick={closeMobileMenu}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* Footer: Quick buttons + Social icons */}
        <div className="mobile-menu__footer">
          <div className="mobile-menu__footer-btns">
            <Link
              to="/quick-donation"
              className="mobile-menu__footer-btn"
              onClick={closeMobileMenu}
            >
              Quick Donate
            </Link>
            {/* <Link
              to="/sri-krishna-janmashtami-2026"
              className="mobile-menu__footer-btn is-saffron"
              onClick={closeMobileMenu}
            >
              ✦ Sri Janmashtami Seva
            </Link> */}
          </div>

          <div className="mobile-menu__footer-social">
            {SOCIAL_LINKS.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noreferrer"
                className="mobile-menu__social-link"
                aria-label={social.label}
              >
                <social.icon />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Dark overlay — click to close mobile menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu__overlay" onClick={closeMobileMenu} />
      )}
    </>
  );
}

export default Header;
